import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Pre-order info — keeps the PDP pre-order UI in sync on variant change.
 *
 * The initial state is rendered by Liquid (blocks/buy-buttons.liquid +
 * snippets/preorder-info.liquid). On variant:update this element re-extracts
 * its replacement from the freshly rendered document and re-asserts the
 * Liquid verdict: product-form.js and notify-back-in-stock.js react to
 * resource.available, which is true for `continue`-policy variants, so they
 * re-enable the wallet / clear is-soldout even when Liquid rendered a
 * pre-order or fail-closed sold-out state. This handler runs after them
 * (macrotask) so the fresh HTML is the last word.
 *
 * FAIL CLOSED: on any error the pre-order UI (class, dataset, this element)
 * is removed and the standard buy buttons remain.
 */
class PreorderInfo extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    this.eventSection = closestSection;
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updatePreorder);
  }

  disconnectedCallback() {
    this.eventSection?.removeEventListener(ThemeEvents.variantUpdate, this.updatePreorder);
    this.eventSection = null;
    clearTimeout(this.syncTimer);
  }

  /**
   * Handles the variant update event.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  updatePreorder = (event) => {
    // product-form.js / notify-back-in-stock.js listen on the same event —
    // defer a tick so this sync runs after them and wins.
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => { if (this.isConnected) this.#sync(event); }, 0);
  };

  /**
   * Re-asserts the freshly rendered Liquid state (pre-order class, cart
   * property dataset, wallet visibility) on the live DOM.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  #sync(event) {
    try {
      if (event.detail.data.newProduct) {
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
        return;
      }

      const html = event.detail.data.html;
      const newInfo = html.querySelector('preorder-info');
      const newButtons = html.querySelector('.product-form-buttons');
      const newFormComponent = html.querySelector('product-form-component');
      const buttons = this.closest('.product-form-buttons');
      const formComponent = this.closest('product-form-component');

      if (newInfo) {
        morph(this, newInfo, { childrenOnly: true });
      }

      const isPreorder = newButtons ? newButtons.classList.contains('is-preorder') : false;
      const isSoldout = newButtons ? newButtons.classList.contains('is-soldout') : false;

      if (buttons && newButtons) {
        buttons.classList.toggle('is-preorder', isPreorder);
        // FAIL CLOSED: re-assert the sold-out swap for continue-policy
        // variants without a valid date (notify-back-in-stock.js cleared it
        // because resource.available is true for them).
        buttons.classList.toggle('is-soldout', isSoldout);
      }

      if (formComponent) {
        const newProperty = newFormComponent?.dataset.preorderProperty;
        if (isPreorder && newProperty) {
          formComponent.dataset.preorderProperty = newProperty;
        } else {
          delete formComponent.dataset.preorderProperty;
        }
      }

      // product-form.js un-hid the wallet for this available=true variant —
      // keep it hidden while a pre-order or sold-out state is active.
      if (isPreorder || isSoldout) {
        formComponent?.querySelector('[ref="acceleratedCheckoutButtonContainer"]')?.setAttribute('hidden', 'true');
      }
    } catch (error) {
      // Degrade to the standard UI — never leave broken pre-order text up.
      try {
        console.error('[preorder-info] sync failed, removing pre-order UI', error);
        this.closest('.product-form-buttons')?.classList.remove('is-preorder');
        const formComponent = this.closest('product-form-component');
        if (formComponent) delete formComponent.dataset.preorderProperty;
        this.remove();
      } catch {
        /* noop */
      }
    }
  }
}

if (!customElements.get('preorder-info')) {
  customElements.define('preorder-info', PreorderInfo);
}
